/**
 * Lecture opérateur de l'inventaire publicitaire.
 *
 * Le backend (`services/ad-inventory.service.ts`) répond en faits : vendu,
 * posé, diffusé, encore posable. Ce module ne fait que les mettre en forme —
 * aucun seuil de confort n'est réinventé ici, et surtout aucun pourcentage de
 * remplissage : la régie lit des passages, pas des taux.
 */
import type { CampaignPacing, PacingVerdict } from './adsApi.js'

export type PacingTone = 'success' | 'warning' | 'danger' | 'muted'

export const PACING_LABELS: Record<PacingVerdict, string> = {
  DONE:        'Complète',
  NOT_STARTED: 'À venir',
  ON_TRACK:    'Tenue',
  TIGHT:       'Marge mince',
  UNREACHABLE: 'Intenable',
  ENDED_SHORT: 'Engagement manqué',
}

const PACING_TONES: Record<PacingVerdict, PacingTone> = {
  DONE:        'success',
  NOT_STARTED: 'muted',
  ON_TRACK:    'success',
  TIGHT:       'warning',
  UNREACHABLE: 'danger',
  ENDED_SHORT: 'danger',
}

export function pacingTone(verdict: PacingVerdict): PacingTone {
  return PACING_TONES[verdict]
}

export const BLOCKER_LABELS: Record<NonNullable<CampaignPacing['blocker']>, string> = {
  NO_USABLE_SPOT: 'aucun spot diffusable',
  FREQUENCY:      'plafond de fréquence atteint',
  INVENTORY:      'écrans insuffisants',
}

/**
 * Géométrie de la barre d'engagement.
 *
 * La piste vaut le volume **vendu**. Le rempli dit où en est la campagne, et le
 * repère de capacité dit jusqu'où l'inventaire restant peut la mener. Le repère
 * court avant la fin de la piste : tout ce qui suit est hors de portée.
 * C'est cette seule image qui remplace l'ancien pourcentage de remplissage.
 */
export interface DeliveryGeometry {
  /** Part diffusée, en % de la piste. */
  airedPct:    number
  /** Part posée en grille, cumulée avec la diffusée, en % de la piste. */
  placedPct:   number
  /** Jusqu'où le posé peut monter avec l'inventaire restant, en % de la piste. */
  capacityPct: number
  /** Passages vendus que l'inventaire restant ne pourra pas absorber. */
  shortfall:   number
}

export function deliveryGeometry(pacing: CampaignPacing): DeliveryGeometry {
  const sold = Math.max(0, pacing.soldSpots)
  if (sold === 0) return { airedPct: 0, placedPct: 0, capacityPct: 0, shortfall: 0 }

  const placed = Math.min(sold, Math.max(0, pacing.placedSpots))
  // Un passage peut être diffusé alors que son placement a depuis été retiré de
  // la grille : le diffusé est borné au posé pour que les segments s'empilent.
  const aired  = Math.min(placed, Math.max(0, pacing.airedSpots))
  const reach  = Math.min(sold, placed + Math.max(0, pacing.reachableSpots))
  const pct    = (value: number) => Math.round((value / sold) * 1000) / 10

  return {
    airedPct:    pct(aired),
    placedPct:   pct(placed),
    capacityPct: pct(reach),
    shortfall:   Math.max(0, pacing.remainingSpots - Math.max(0, pacing.reachableSpots)),
  }
}

/**
 * Ce qui demande une décision aujourd'hui. Chaque compte est un filtre de la
 * liste, jamais une vignette décorative : un chiffre qu'on ne peut pas ouvrir
 * n'a rien à faire en tête de la régie.
 */
export interface AttentionCounts {
  unreachable: number
  tight:       number
  endedShort:  number
}

export type AttentionKey = keyof AttentionCounts

export const ATTENTION_VERDICTS: Record<AttentionKey, PacingVerdict> = {
  unreachable: 'UNREACHABLE',
  tight:       'TIGHT',
  endedShort:  'ENDED_SHORT',
}

export function attentionCounts(pacings: CampaignPacing[]): AttentionCounts {
  const counts: AttentionCounts = { unreachable: 0, tight: 0, endedShort: 0 }
  for (const pacing of pacings) {
    if (pacing.verdict === 'UNREACHABLE') counts.unreachable += 1
    else if (pacing.verdict === 'TIGHT') counts.tight += 1
    else if (pacing.verdict === 'ENDED_SHORT') counts.endedShort += 1
  }
  return counts
}

export function hasAttention(counts: AttentionCounts): boolean {
  return counts.unreachable > 0 || counts.tight > 0 || counts.endedShort > 0
}

/**
 * La pression n'alarme qu'au-delà de la saturation : sous 100 %, une campagne
 * verte l'est vraiment ; au-delà, les campagnes vertes se disputent le même
 * écran et le vert devient un mensonge poli.
 */
export function pressureTone(pressurePct: number): PacingTone {
  if (pressurePct > 100) return 'danger'
  if (pressurePct > 85)  return 'warning'
  return 'success'
}

/** « 4 spots » / « 1 spot », sans avoir à répéter l'accord partout. */
export function spotCount(value: number): string {
  return `${value} spot${value > 1 ? 's' : ''}`
}
